import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";

const SYMBOLS = ["🍗", "🐔", "🥩", "⭐", "🍀", "💰", "7️⃣"];
const STOPS = [0.9, 1.2, 1.5];
const H = 84;

function Reel({ final, dur, idx }) {
  const strip = useMemo(() => [...Array(14)].map((_, i) => SYMBOLS[(i * 3 + idx * 2) % SYMBOLS.length]).concat(final), [final, idx]);
  return (
    <div className="w-[84px] h-[84px] overflow-hidden rounded-2xl bg-white shadow-inner border-4 border-amber-300">
      <div className="slot-strip" style={{ "--end": `-${(strip.length - 1) * H}px`, animationDuration: `${dur}s` }}>
        {strip.map((s, i) => <div key={i} className="h-[84px] grid place-items-center text-5xl">{s}</div>)}
      </div>
    </div>
  );
}

function Confetti() {
  const bits = useMemo(() => [...Array(60)].map((_, i) => ({
    left: Math.random() * 100, delay: 1.5 + Math.random() * 0.8, dur: 1.6 + Math.random() * 1.4,
    color: ["#f59e0b", "#14b8a6", "#ef4444", "#22c55e", "#3b82f6", "#eab308"][i % 6], round: i % 4 === 0,
  })), []);
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden z-[60]">
      {bits.map((b, i) => <span key={i} className="slot-confetti" style={{ left: `${b.left}%`, animationDelay: `${b.delay}s`, animationDuration: `${b.dur}s`, background: b.color, borderRadius: b.round ? "999px" : "2px" }} />)}
    </div>
  );
}

function Message({ win, streak }) {
  if (!win) return (
    <>
      <div className="font-heading font-extrabold text-2xl text-white">Filed, nearly a jackpot!</div>
      <p className="text-amber-100 text-sm mt-1" data-testid="slot-message">Finish giving every kilo a home to hit the jackpot. Your streak starts again on your next balanced log.</p>
    </>
  );
  return (
    <>
      <div className="font-heading font-extrabold text-3xl text-white">JACKPOT! 🎉</div>
      <p className="text-amber-100 text-sm mt-1" data-testid="slot-message">Balanced log filed. Every kilo has a home.</p>
      <div data-testid="slot-streak" className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-white font-bold">
        🔥 {streak > 1 ? `${streak} balanced logs in a row!` : "1 balanced log: a new streak starts!"}
      </div>
    </>
  );
}

export function SlotCelebration({ win, streak, onDone }) {
  const [stopped, setStopped] = useState(false);
  useEffect(() => { const t = setTimeout(() => setStopped(true), 1650); return () => clearTimeout(t); }, []);
  const finals = win ? ["7️⃣", "7️⃣", "7️⃣"] : ["7️⃣", "7️⃣", "🍗"];
  return (
    <div data-testid="slot-celebration" className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm grid place-items-center p-6">
      {win && <Confetti />}
      <div className="w-full max-w-sm rounded-[28px] bg-gradient-to-b from-rose-600 to-amber-600 p-5 text-center shadow-2xl border-4 border-amber-300">
        <div className="text-amber-200 font-heading font-extrabold tracking-[0.3em] text-xs mb-3">G&amp;L ONE · LOG FILED</div>
        <div className="flex justify-center gap-2 bg-slate-900/40 rounded-2xl p-3" data-testid="slot-reels">
          {finals.map((f, i) => <Reel key={i} final={f} dur={STOPS[i]} idx={i} />)}
        </div>
        <div className={`mt-4 transition-opacity duration-300 ${stopped ? "opacity-100" : "opacity-0"}`}>
          <Message win={win} streak={streak} />
          <Button data-testid="slot-continue" onClick={onDone} className="mt-4 w-full h-12 rounded-2xl bg-white text-rose-700 hover:bg-amber-50 font-bold text-base">Continue</Button>
        </div>
      </div>
    </div>
  );
}
